import type {ReactElement} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {HeaderSize, Panel, PanelContent, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import type {PanelFooterProps, PanelHeaderProps} from '@Components/panel';
import type {TabKey} from '@Components/tabs';
import {useSelectedTab} from '@Components/tabs';
import type {
  CarouselData,
  SelectedFilter,
  SourceTier,
  StockButtonFilters,
  StockFilter,
  StockFilterID,
  StockPrimaryCTA,
  StockResponseCarousel,
  StockItemDelivery,
  StockSelectionUpload,
} from '@Panels/stock-panel/stock-panel.types';
import {
  AI_CREATE_WITH_AI_BUTTON_ID,
  AI_CREATE_WITH_AI_BUTTON_WIDTH,
  AiCarouselContentType,
  AudioFilters,
  CAROUSEL_ITEM_HEIGHT,
  MAX_CAROUSEL_ITEM_ASPECT_RATIO,
  STOCK_GRID_ITEM_DIMENSION,
  DEFAULT_APPLIED_SUBFILTER,
  MIN_STOCK_ITEMS_TO_DISPLAY,
  SCROLL_SHADOW_THRESHOLD,
  STOCK_PANEL_AI_IMAGE_PANEL_ID,
  STOCK_PANEL_AI_VIDEO_PANEL_ID,
  STOCK_PANEL_AI_VOICE_PANEL_ID,
  STOCK_PANEL_ADD_LOADING_KEY,
  STOCK_PANEL_TAB_ID,
  STOCK_CAROUSEL_TYPE_TO_ID,
  STOCK_CAROUSEL_TYPE_TO_TAB,
  STOCK_SUBFILTER_TO_ADDITIONAL_FILTERS_MAP,
  STOCK_TABS_TO_CAROUSEL_TYPE_MAPPING,
  StockCarouselType,
  StockPanelTabs,
} from '@Panels/stock-panel/stock-panel.types';
import {Text, TextSize, TextType} from '@Components/text';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {getRandomNumberInRange} from '@Utils/math.util';
import type {AITextToImageBackendData, AudioCategoryBackendData} from '@Libraries/pmw-stock-media-library';
import {
  getGeneratedImagesForCarousel,
  getRecentStockItems,
  getStockForCarousel,
  getStockLibraryAudioCategories,
  setRecentStockItem,
  StockSources,
} from '@Libraries/pmw-stock-media-library';
import {ingestStockScoreDebug, isStockScoreDebugActive, primeStockScoreDebugAvailability, resetStockScoreDebug} from '@Panels/stock-panel/stock-score-debug';
import Carousel from '@Components/carousel/carousel';
import type {
  PMWStockBackendAiAnimatedSticker,
  PMWStockBackendAiIcon,
  PMWStockBackendAiSticker,
  PMWStockBackendAnimatedSticker,
  PMWStockBackendExtractedGettySticker,
  PMWStockBackendIcon,
  PMWStockBackendShape,
  PMWStockBackendStillSticker,
} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import {
  createKeepFiltersHiddenForTab,
  FALLBACK_GRID_ITEM_HEIGHT,
  FALLBACK_GRID_ITEM_WIDTH,
  getAiCarouselItemMeta,
  getCarouselDataForAiIconItem,
  getCarouselDataForAiVideoItem,
  getCarouselDataForAiStickerItem,
  getCarouselDataForAudioItem,
  getCarouselDataForClipartItem,
  getCarouselDataForExtractedGettyStickerItem,
  getCarouselDataForIconItem,
  getCarouselDataForImageItem,
  getCarouselDataForShapeItem,
  getCarouselDataForAiAnimatedStickerItem,
  getCarouselDataForStickerItem,
  getCarouselDataForStillStickerItem,
  getCarouselDataForVideoItem,
  getCarouselTabOrder,
  getCarouselTitleText,
  getDefaultSelectedFilterForTab,
  withSeasonalFilterResetForSearch,
  getGATrackingFilterTerm,
  removeSubFiltersExclusiveWith,
  getGATrackingSearchTerm,
  getGATrackingSourceFromMediaSource,
  getAiSkeletonItem,
  getGATrackingTypeFromMediaType,
  getGridID,
  getGridItemForAIGeneratedImageV2,
  getGridItemForBackendData,
  getHasUserSeenMobileVideoPreview,
  getOptionsForStockGetter,
  getSearchBarPlaceholderText,
  getStockFilterForAudioFilterBackendData,
  getStockTabsOrder,
  getTestIdForCarouselType,
  shouldShowShowMoreCTA,
  isAudioStockItem,
  isDesktopAudioAdd,
  isAiEditedImageGridItem,
  isGettyLicensedGridItem,
  isRecentStockItemAllowed,
  markUserHasSeenMobileVideoPreview,
  STOCK_AUDIO_GRID_ITEM_WIDTH,
  preFillAiPromptBoxesForAiPanelKey,
  deliverStockItems,
  uploadStockSelection,
  trackAiToolMenuOpenFromMediaDrawer,
} from '@Panels/stock-panel/stock-panel.library';
import {getSeasonalFilter, getStockTabsToFilterMapping} from '@Panels/stock-panel/stock-panel-filters.library';
import {clearPendingAiPromptBoxPreFills} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pre-fill.library';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import type {BasePanel, PanelGrowlData} from '@Components/panel/panel.types';
import {PanelDataKeys} from '@Components/panel/panel.types';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import type {SOURCE} from '@Libraries/stock-graphic-library';
import {validateGettyLicenseAgreement} from '@Panels/premium-agreement-panel/premium-agreement.library';
import {hideLoading} from '@Libraries/loading-toast-library';
import type {ElementData} from '@Libraries/add-media-library';
import {createGridItemDragHandler} from '@Libraries/canvas-item-drag.library';
import {StockPanelSearchBar} from '@Panels/stock-panel/components/stock-panel-search-bar/stock-panel-search-bar';
import StockPanelGrid from '@Panels/stock-panel/components/stock-panel-grid';
import {setRecentStockAvailability, setStockGetterOptions} from '@Panels/stock-panel/stock-panel-reducer';
import {
  addItemsToGrid,
  clearSelectedItems,
  deleteGridItems,
  getSelectedGridItems,
  reloadGrid,
  replaceGridItem,
  toggleGridItemSelection,
  updateMaxSelectionNumberForGrid,
} from '@Components/base-grid';
import StockPanelFooter from '@Panels/stock-panel/components/stock-panel-footer';
import {StockMediaFilters} from '@Panels/stock-panel/components/stock-media-filters/stock-media-filters';
import {StockMediaTabs} from '@Panels/stock-panel/components/stock-media-tabs/stock-media-tabs';
import {StockPanelMessage} from '@Panels/stock-panel/components/stock-panel-message/stock-panel-message';
import {StockAiButton} from '@Panels/stock-panel/components/stock-ai-button/stock-ai-button';
import type {AiPanelKey} from '@Panels/stock-panel/components/stock-ai-button/stock-ai-button.types';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {deleteTabStateForId, selectTab} from '@Components/tabs/tabs-reducer';
import {getUserId} from '@Libraries/user.library';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {CarouselItemData} from '@Components/carousel/carousel.types';
import {CarouselClickBehaviour} from '@Components/carousel/carousel.types';
import {TextToImageGrid} from '@Components/text-to-image-grid';
import type {GridStockImageItemStorage} from '@Components/base-grid/components/grid-stock-image-item';
import type {GridClipartItemBackEndData} from '@Components/base-grid/components/grid-clipart-item';
import {SkeletonAnimationType} from '@Components/base-grid/components/grid-skeleton-item';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {TextToImageGridProps} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {AiImageState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {AnimatePresence, motion} from 'framer-motion';
import {pauseAllAudioGridItems} from '@Components/base-grid/components/grid-user-audio-item/grid-audio-item-reducer';
import {isMobile} from 'react-device-detect';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './stock-panel.module.scss';
import {updatePanelData} from '@Components/panel/panel-reducer';
import type {AiImageAspectRatioOptions} from '@Panels/ai-image-panel/ai-image-panel.types';
import {MediaTypePrompt} from '@Panels/ai-image-panel/ai-image-panel.types';
import type {Dimensions} from '@floating-ui/react';
import AiImagePromptBox from '@Panels/ai-image-panel/components/ai-image-prompt-box/ai-image-prompt-box';
import {GeneratedImageItemMenu} from '@Components/text-to-image-grid/components/generated-image-item-menu/generated-image-item-menu';
import {openEditWithAiForActiveImage} from '@Libraries/text-to-image-library';
import AiImagePanel from '@Panels/ai-image-panel';
import AiVideoPanel from '@Panels/ai-video-panel';
import AiVoicePanel from '@Panels/ai-voice-panel';
import StockAiPanel from '@Panels/stock-ai-panel/stock-ai-panel';
import {STOCK_AI_PANEL_ID} from '@Panels/stock-ai-panel/stock-ai-panel.types';
import type {GenerateAISpeechResponse} from '@Libraries/text-to-speech.library';
import {addGeneratedVoicesToDesign, addGeneratedVoiceToDesign, getUserGeneratedSpeech} from '@Libraries/text-to-speech.library';
import {getGeneratedAiVideos} from '@Libraries/ai-video.library';
import {getAiImageCarouselItem, getAiVoiceCarouselItem} from '@Panels/stock-panel/stock-panel-ai-carousel.helper';
import {v4 as uuidv4} from 'uuid';
import {getDimensionsForAiImageSkeletonItem} from '@Panels/ai-image-panel/ai-image-panel-library';
import {noop} from '@Utils/general.util';
import {getCarouselTiersForTabs, getTieredSourcesForSelectedTab} from '@Panels/stock-panel/stock-panel-request-params.helper';

const MOBILE_VIDEO_PREVIEW_GROWL_VISIBLE_DURATION = 3000;
export interface StockPanelProps extends BasePanel {
  preSelectedFilters?: SelectedFilter[];
  keepFiltersHiddenForTab?: Record<StockPanelTabs, boolean>;
  /** Restricts which filter pills a tab renders (e.g. only Pricing on Stickers while replacing a sticker). Tabs not listed keep all their filters. */
  allowedFiltersForTab?: Partial<Record<StockPanelTabs, StockFilterID[]>>;
  tabsToShow?: StockPanelTabs[];
  defaultSearchTab?: StockPanelTabs;
  maxSelectionNumber?: number;
  selectionID?: string;
  /** Deliver the whole selection to onAddSelected in one call (e.g. N picks become one slideshow). The default streams each item as soon as its upload settles. */
  addInBulk?: boolean;
  shouldLoadDataUrlImage?: boolean;
  populateRecentlyAddedOnItemAdd?: boolean;
  /** Hides the Recents carousel on the All browse view (e.g. the QR logo picker, where stock recents are not logo recents). */
  shouldShowRecentsCarousel?: boolean;
  videoQualityFiltersDisabled?: boolean;
  tabId?: string;
  stockTabToGridIdMapping?: Record<StockPanelTabs, string>;
  onStartAddSelected?: (numItems?: number) => void;
  isManageappPage?: boolean;
  blacklistedSources?: StockSources[];
  customTieredSources?: SourceTier[];
  bypassCompoundedView?: boolean;
  enableMultiSelection?: boolean;
  selectionFooterCtaText?: string;
  enableDragToCanvas?: boolean;
  aiCarouselContentTypes?: AiCarouselContentType[];
  defaultAiImageMediaType?: MediaTypePrompt;
  showEditWithAiOnGeneratedImages?: boolean;
  desktopHeaderTitle?: string;
  /** Replaces the default header with the AI Images one (gradient title, back, close) matching the AI Voice/Video panels — for hosts showing this panel as their AI Images view (e.g. the uploads modal). */
  showAiImagesHeader?: boolean;
  initialAiPanel?: AiPanelKey;
  shouldReplaceOnGenerate?: boolean;
  isPopupOpen?: boolean;

  onAddSelected(items: ElementData[]): void | Promise<void>;

  /** Overrides what picking an AI voice does — replace flows swap the selected audio instead of adding a clip. */
  onAddVoiceItem?(idGeneratedVoice: number): Promise<void>;

  onBack?(): void;

  onClose?(): void;

  onAiCtaClicked?: (key: AiPanelKey) => void;
}

const [MIN_SKELETON_WIDTH, MAX_SKELETON_WIDTH] = [80, 220];
const TOTAL_SKELETONS_PER_ROW = 10;
// Matches the XSMALL title text's 16px line-height so the header row keeps its height while loading.
const TITLE_SKELETON_HEIGHT = 16;
const TITLE_SKELETON_WIDTH = 80;
const DEFAULT_SELECTION_ID = 'STOCK_MEDIA_MODAL_SELECTION_ID';
const query: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 640,
  },
  large: {
    minWidth: 641,
  },
};
const AI_VOICE_CAROUSEL_FETCH_LIMIT = 20;

export default function StockPanel({
  populateRecentlyAddedOnItemAdd = true,
  shouldShowRecentsCarousel = true,
  tabsToShow = getStockTabsOrder(),
  videoQualityFiltersDisabled = false,
  onStartAddSelected = noop,
  tabId = STOCK_PANEL_TAB_ID,
  preSelectedFilters = [],
  keepFiltersHiddenForTab = createKeepFiltersHiddenForTab(),
  isManageappPage = false,
  bypassCompoundedView = false,
  enableMultiSelection = false,
  enableDragToCanvas = false,
  aiCarouselContentTypes = [],

  ...props
}: StockPanelProps): ReactElement<any> {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isGridNoResults, setIsGridNoResults] = useState(false);
  const [aiCarouselLoaded, setAiCarouselLoaded] = useState(false);
  const [mobileAiPanelOpen, setMobileAiPanelOpen] = useState<AiPanelKey | null>(props.initialAiPanel ?? null);
  const [mobileAiToolReturnTo, setMobileAiToolReturnTo] = useState<AiPanelKey | null>(null);
  // True only while showing the panel opened by initialAiPanel — any in-panel navigation clears it (a navigated-to panel has somewhere to go Back to).
  const isAiPanelOpenedDirectly = useRef(Boolean(props.initialAiPanel));
  const panelId = props.panelId ?? 'STOCK_MEDIA_PANEL_NO_ID_PASSED';
  const selectedTab = useSelectedTab(tabId, props.defaultSearchTab ?? tabsToShow[0]) as StockPanelTabs;
  const stockTabsToFilterMapping = useRef<Record<StockPanelTabs, StockFilter[]>>(getStockTabsToFilterMapping(videoQualityFiltersDisabled));
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>(preSelectedFilters);
  const [loaded, setLoaded] = useState(false);
  // Distinct from "no results": the carousel fetch itself failed, so the remedy is to retry the same
  // search rather than to change it. Kept separate from `loaded` because `loaded` only says the
  // spinning stopped, not whether there is anything to show.
  const [carouselFetchFailed, setCarouselFetchFailed] = useState(false);
  const [recentStockLoaded, setRecentStockLoaded] = useState(false);
  // Audio categories land in the stockTabsToFilterMapping ref; this state re-renders an already-open Audios tab's filter row when they arrive (#3272).
  const [, setAreAudioCategoriesLoaded] = useState(false);
  const carouselData = useRef<CarouselData>({});
  // The search bar's visible text (typed but possibly not submitted) — it pre-fills the AI prompt boxes on Create-with-AI.
  const typedSearchText = useRef('');
  // Adds mark the Recents carousel stale; the refresh is deferred to popup close so open carousels never mutate live.
  const recentsNeedRefresh = useRef(false);
  const panelContentRef = useRef<HTMLDivElement>(null);
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  // Whether the aggregated AI carousel will show at all — its data needs a logged-in user. Render decisions key on
  // this (with a loading skeleton) rather than on data arrival, so no other row flashes in while it loads.
  const isAiCarouselEnabled = aiCarouselContentTypes.length > 0 && isUserLoggedIn.current;
  const stockSubfilterToAdditionalFiltersMapping = useRef(STOCK_SUBFILTER_TO_ADDITIONAL_FILTERS_MAP);
  const audioFilterIDs = useRef<(StockButtonFilters | string)[]>(AudioFilters);
  const lastSearchTerm = useRef<string | undefined>(undefined);
  const pendingAiGenerationSkeletonIds = useRef<string[]>([]);

  const clearSelection = (): void => {
    dispatch(
      clearSelectedItems({
        gridSelectionGroupId: carouselItemsSelectionID.current,
      })
    );
  };
  const onHide = (): void => {
    clearSelection();
    dispatch(pauseAllAudioGridItems());
  };
  const defaultPanelClose = useDefaultPanelClose(panelId, props.hideOnClose, onHide);
  const onClose = props.onClose ?? defaultPanelClose;

  // Both resumable hosts keep this panel mounted while closed, each with its own visibility signal: the desktop
  // popup toggles isPopupOpen, the mobile resumable modal hides the panel in Redux. Combining them gives one
  // "user is looking at the media modal" flag, so every open is tracked — not just the first mount.
  const isPanelHiddenInResumableModal = useAppSelector((state) => {
    return !!state.panels.panelHashmap[panelId]?.isHidden;
  });
  const isStockPanelVisible = props.isPopupOpen !== false && !isPanelHiddenInResumableModal;

  useEffect(() => {
    if (isStockPanelVisible) {
      trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_OPEN);
      return;
    }
    // The panel stays mounted while hidden, so a preview that was playing would keep going behind the editor.
    dispatch(pauseAllAudioGridItems());
  }, [isStockPanelVisible]);

  const carouselItemsSelectionID = useRef(props.selectionID ?? DEFAULT_SELECTION_ID);
  const selectedStockItems = useAppSelector((state) => {
    return getSelectedGridItems(state, carouselItemsSelectionID.current);
  });
  const isRecentStockAvailble = useAppSelector((state) => {
    return state.stockPanel.isRecentStockAvailable;
  });
  const aiImageState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiImage;
  });
  const params = useContainerQuery(panelContentRef, query);
  const isSmall = !params.large;
  const dispatch = useAppDispatch();
  const scrollingContainerRef = useRef<HTMLDivElement>(null);
  const [showFilterShadow, setShowFilterShadow] = useState(false);
  const updatedBlacklistedSources = useMemo(() => {
    return props.blacklistedSources ?? [];
  }, [props.blacklistedSources]);

  const resetTabFiltersAndViewMode = (): void => {
    dispatch(selectTab(tabId, props.defaultSearchTab ?? tabsToShow[0]));
    setSelectedFilters([]);
  };

  const resetToDefaultView = (): void => {
    resetTabFiltersAndViewMode();
    setSearchTerm(undefined);
  };

  const onBackButton = (): void => {
    setIsSearchLoading(false);
    if ((!isGridView() || tabsToShow.length === 1) && !!props.onBack) {
      props.onBack();
    } else {
      resetToDefaultView();
    }
  };

  // The search bar's clear (X) only dismisses the active search and returns to the default view. Unlike the mobile
  // header's back arrow (onBackButton), it never navigates to the host's previous screen via props.onBack.
  const onClearSearch = (): void => {
    setIsSearchLoading(false);
    resetToDefaultView();
  };

  const backToStockPanel = (): void => {
    isAiPanelOpenedDirectly.current = false;
    setMobileAiPanelOpen(null);
  };

  useEffect(() => {
    trackAiToolMenuOpenFromMediaDrawer(mobileAiPanelOpen);
  }, [mobileAiPanelOpen]);

  const openAiPanelFromNavigation = (key: AiPanelKey): void => {
    isAiPanelOpenedDirectly.current = false;
    setMobileAiPanelOpen(key);
  };

  // From the dedicated AI panel, opening a single tool remembers to return there on Back (rather than the stock panel).
  const onOpenAiToolFromAiPanel = (key: AiPanelKey): void => {
    setMobileAiToolReturnTo('ai-all');
    openAiPanelFromNavigation(key);
  };

  const onAiToolBack = (): void => {
    if (mobileAiToolReturnTo) {
      openAiPanelFromNavigation(mobileAiToolReturnTo);
      setMobileAiToolReturnTo(null);
      return;
    }
    backToStockPanel();
  };

  // When no host wires up onAiCtaClicked (e.g. the slideshow inline panels), StockPanel hosts the AI panel swap itself on every device.
  // An active search pre-fills the AI prompt boxes first, so the generator opens with the searched term pre-typed.
  const aiCtaHandler = (key: AiPanelKey): void => {
    if (key === 'ai-stickers') {
      trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_CREATE_AI_STICKERS);
    }
    preFillAiPromptBoxesForAiPanelKey(key, typedSearchText.current.trim() !== '' ? typedSearchText.current : searchTerm);
    (props.onAiCtaClicked ?? openAiPanelFromNavigation)(key);
  };

  // The backend read-modify-writes the recents hash, so the writes stay sequential; they overlap the delivery and only need to finish before the toast hides.
  const updateRecentStock = async (uploads: StockSelectionUpload[]): Promise<void> => {
    if (!populateRecentlyAddedOnItemAdd || !isUserLoggedIn.current) {
      return;
    }
    for (const upload of uploads) {
      if (upload.uploadedItem === null || getAiCarouselItemMeta(upload.item) || isAiEditedImageGridItem(upload.item)) {
        continue;
      }
      try {
        // eslint-disable-next-line no-await-in-loop -- parallel writes would race the backend's read-modify-write
        await setRecentStockItem(upload.item);
        recentsNeedRefresh.current = true;
      } catch (e) {
        console.error(e);
      }
    }
  };

  const deliverToHost: StockItemDelivery = async (itemsToDeliver): Promise<void> => {
    await props.onAddSelected(itemsToDeliver);
  };

  const onCTA: StockPrimaryCTA = async (items, dropPosition): Promise<void> => {
    onStartAddSelected(items.length);

    const gettyAgreement = items.some(isGettyLicensedGridItem) ? await validateGettyLicenseAgreement(onClose) : false;
    const permittedItems = items.filter((gridItem) => !isGettyLicensedGridItem(gridItem) || gettyAgreement);

    try {
      const {uploads, uploadedItems} = await uploadStockSelection({
        items: permittedItems,
        loadingKey: STOCK_PANEL_ADD_LOADING_KEY,
        addInBulk: Boolean(props.addInBulk),
        onDeliver: deliverToHost,
        shouldLoadDataUrlImage: props.shouldLoadDataUrlImage,
        dropPosition,
        onUploadsStarting: (itemsToUpload): void => {
          itemsToUpload.forEach((gridItem) => {
            trackPosterBuilderGA4Events(`MediaModal_${getGATrackingSourceFromMediaSource(gridItem.source as SOURCE) + getGATrackingTypeFromMediaType(gridItem.type)}Add`);
          });
        },
      });

      const recentStockUpdate = updateRecentStock(uploads);

      // Bulk hosts (slideshow / create-from-scratch / manageapp) need the whole selection in one call; the default streams each item as its upload settles.
      if (props.addInBulk && uploadedItems.length > 0) {
        await deliverStockItems(uploadedItems, deliverToHost);
      }

      await recentStockUpdate;
    } finally {
      // uploadStockSelection raised the toast, so it has to come down on every exit or it hangs over the panel.
      hideLoading(STOCK_PANEL_ADD_LOADING_KEY);
    }

    if (isDesktopAudioAdd(items)) {
      onClose();
    }
  };

  const onItemPointerDown = createGridItemDragHandler<GridItemStorage>((item, dropPosition) => onCTA([item], dropPosition), isAudioStockItem);

  const getGridIdForTab = (tab: StockPanelTabs): string => {
    if (props.stockTabToGridIdMapping) {
      return props.stockTabToGridIdMapping[tab];
    }
    return getGridID(tab);
  };

  const onTabClicked = (tabKey: TabKey): void => {
    if (selectedTab.valueOf() !== tabKey) {
      // The ALL tab is the carousel browse view; it has no grid to reset when leaving it.
      if (selectedTab !== StockPanelTabs.ALL) {
        dispatch(
          reloadGrid({
            gridId: getGridIdForTab(selectedTab),
            gridSelectionGroupId: carouselItemsSelectionID.current,
          })
        );
      }
      dispatch(selectTab(tabId, tabKey));
      setSelectedFilters(getDefaultSelectedFilterForTab(tabKey as StockPanelTabs, searchTerm));
      dispatch(setStockGetterOptions({}));
    }
  };

  const deleteAiSkeleton = (skeletonItemId: string | undefined): void => {
    if (skeletonItemId === undefined) {
      return;
    }
    dispatch(
      deleteGridItems({
        gridId: getGridIdForTab(StockPanelTabs.TEXT_TO_IMAGE),
        gridSelectionGroupId: carouselItemsSelectionID.current,
        itemIdsToDelete: [skeletonItemId],
      })
    );
  };

  const onGeneratedImagesReceived = (response: AITextToImageBackendData[]): void => {
    const skeletonItemId = pendingAiGenerationSkeletonIds.current.shift();
    const [generatedImage] = response.map(
      (backendItem): GridStockImageItemStorage => ({
        ...getGridItemForAIGeneratedImageV2(backendItem),
        topRightContent: getAiImageItemTopRightContent(backendItem),
      })
    );
    if (!generatedImage) {
      deleteAiSkeleton(skeletonItemId);
      return;
    }
    const gridId = getGridIdForTab(selectedTab);
    if (skeletonItemId === undefined) {
      dispatch(addItemsToGrid({id: gridId, index: 0, items: [generatedImage]}));
      return;
    }
    dispatch(replaceGridItem({gridId, itemId: skeletonItemId, gridItem: generatedImage}));
  };

  const addLoadingSkeleton = (aspectRatio: AiImageAspectRatioOptions): void => {
    const skeletonItemId = uuidv4();
    dispatch(
      addItemsToGrid({
        id: getGridIdForTab(StockPanelTabs.TEXT_TO_IMAGE),
        index: 0,
        items: [
          {
            ...getAiSkeletonItem(skeletonItemId),
            ...getDimensionsForAiImageSkeletonItem(aspectRatio),
            animationType: SkeletonAnimationType.AI,
          },
        ],
      })
    );
    pendingAiGenerationSkeletonIds.current.push(skeletonItemId);
  };

  const removeLoadingSkeleton = (): void => {
    deleteAiSkeleton(pendingAiGenerationSkeletonIds.current.shift());
  };

  const getSearchBarContainer = (): ReactElement<any> => {
    if (isTextToImageTabActive()) {
      const mediaType = props.defaultAiImageMediaType;
      return (
        <AiImagePromptBox
          onImageGenerationStarted={addLoadingSkeleton}
          onImageGenerationSuccessful={onGeneratedImagesReceived}
          onImageGenerationFailed={removeLoadingSkeleton}
          mediaType={mediaType}
        />
      );
    }
    return (
      <StockPanelSearchBar
        isLoading={isSearchLoading}
        onBack={onClearSearch}
        onSearch={(updatedSearchTerm) => {
          if (updatedSearchTerm === searchTerm) {
            return;
          }
          setIsSearchLoading(Boolean(updatedSearchTerm));
          setSearchTerm((prevState) => {
            lastSearchTerm.current = prevState;
            return updatedSearchTerm;
          });
        }}
        placeholderText={getSearchBarPlaceholderText(isGridView() ? selectedTab : undefined)}
        val={searchTerm}
        onInputTextChange={(text): void => {
          typedSearchText.current = text;
        }}
      />
    );
  };

  const onFilterChanged = (filterID: StockFilterID, subFilterID: string | null): void => {
    let requiredFilters = selectedFilters.filter((selectedFilter) => {
      return selectedFilter.filter !== filterID.valueOf();
    });

    if (subFilterID !== null && subFilterID !== DEFAULT_APPLIED_SUBFILTER.id) {
      if (selectedTab === StockPanelTabs.SHAPES) {
        trackPosterBuilderGA4Events(GA4EventName.MediaModal_ShapeFilter, {[GA4EventParamName.VALUE]: subFilterID});
      }
      requiredFilters.push({
        filter: filterID,
        subFilter: subFilterID,
      });

      // Contradictory selections (Getty stickers are stills only, so Getty × Animated) can't both apply:
      // picking one side deselects the other instead of disabling it.
      requiredFilters = removeSubFiltersExclusiveWith(selectedTab, filterID.valueOf(), subFilterID, requiredFilters);
    }

    const filterIDsToRemove: StockFilterID[] = Object.keys(stockSubfilterToAdditionalFiltersMapping.current[selectedTab][filterID] ?? [])
      .map((key) => {
        return stockSubfilterToAdditionalFiltersMapping.current[selectedTab][filterID][key].map((stockFilter) => {
          return stockFilter.id;
        });
      })
      .flat();

    requiredFilters = requiredFilters.filter((selectedfilter) => {
      return !filterIDsToRemove.includes(selectedfilter.filter as StockFilterID);
    });

    setSelectedFilters(requiredFilters);
  };

  const clickBehaviour: CarouselClickBehaviour = enableMultiSelection ? CarouselClickBehaviour.SELECT : CarouselClickBehaviour.ADD;

  const onGridItemClicked = (item: GridItemStorage): void => {
    if (enableMultiSelection) {
      dispatch(
        toggleGridItemSelection({
          gridItemId: item.id,
          gridSelectionGroupId: carouselItemsSelectionID.current,
        })
      );
      return;
    }
    void onCTA([item]);
  };

  const onSelectionFooterPrimaryAction = async (items: GridItemStorage[]): Promise<void> => {
    onClose();
    await onCTA(items);
  };

  const getFooter = (): ReactElement<PanelFooterProps> | undefined => {
    if (!enableMultiSelection) {
      return undefined;
    }
    if (props.showAiImagesHeader) {
      return getAiImagesFooter();
    }
    return (
      <StockPanelFooter
        selectionGroupID={carouselItemsSelectionID.current}
        ctaText={props.selectionFooterCtaText ?? ''}
        showCount={props.maxSelectionNumber !== 1}
        onPrimaryAction={onSelectionFooterPrimaryAction}
        makeBottomPillsScrollable={false}
      />
    );
  };

  // Mirrors the AI Videos panel footer so the uploads modal's AI tools share one Add Selected treatment.
  const getAiImagesFooter = (): ReactElement<PanelFooterProps> | undefined => {
    if (selectedStockItems.length === 0) {
      return undefined;
    }
    return (
      <PanelFooter
        fullWidthPrimaryActions={isMobile}
        primaryActions={[
          <PanelFooterPrimaryDefaultAction
            key="btn-add-selected-ai-images"
            text={window.i18next.t('pmwjs_add_selected')}
            onClick={(): void => {
              void onSelectionFooterPrimaryAction(selectedStockItems);
            }}
          />,
        ]}
      />
    );
  };

  const onEditWithAiSelected = async (item: AITextToImageBackendData): Promise<void> => {
    onClose();
    await onCTA([getGridItemForAIGeneratedImageV2(item)]);
    openEditWithAiForActiveImage();
  };

  const getAiImageItemTopRightContent = (item: AITextToImageBackendData): ReactElement | undefined => {
    if (isMobile) {
      return undefined;
    }
    return (
      <GeneratedImageItemMenu
        item={item}
        gridId={getGridIdForTab(StockPanelTabs.TEXT_TO_IMAGE)}
        gridSelectionGroupId={carouselItemsSelectionID.current}
        onEditWithAi={
          props.showEditWithAiOnGeneratedImages
            ? (itemToEdit): void => {
                void onEditWithAiSelected(itemToEdit);
              }
            : undefined
        }
      />
    );
  };

  const getAIImageGrid = (gridItemID: string): ReactElement<any> => {
    const gridProps: TextToImageGridProps = {
      customOnGridItemClicked: onGridItemClicked,
      gridClasses: 'spacing-p-t-6 spacing-p-b-6 spacing-p-r-3',
      gridSelectionGroupId: carouselItemsSelectionID.current,
      maxSelectionNumber: props.maxSelectionNumber,
      gridId: gridItemID,
      isLoading: aiImageState === AiImageState.GENERATING,
      getItemTopRightContent: getAiImageItemTopRightContent,
    };

    return <TextToImageGrid key={gridItemID} {...gridProps} />;
  };

  const getCustomDimensionForGridItems = (): Dimensions | undefined => {
    if (selectedTab === StockPanelTabs.AUDIOS) {
      return {
        width: 250,
        height: STOCK_GRID_ITEM_DIMENSION,
      };
    }
    if (selectedTab === StockPanelTabs.SHAPES) {
      return {
        width: STOCK_GRID_ITEM_DIMENSION,
        height: STOCK_GRID_ITEM_DIMENSION,
      };
    }
    return;
  };

  const getStockGrid = (): ReactElement<any> => {
    const gridItemID = getGridIdForTab(selectedTab);
    if (selectedTab === StockPanelTabs.TEXT_TO_IMAGE) {
      return getAIImageGrid(gridItemID);
    }

    return (
      <StockPanelGrid
        isManageappPage={isManageappPage}
        key={gridItemID}
        onSingleItemClick={onCTA}
        searchTerm={searchTerm}
        selectionGroupID={carouselItemsSelectionID.current}
        maxSelectionNumber={props.maxSelectionNumber}
        gridID={gridItemID}
        sources={props.customTieredSources ?? getTieredSourcesForSelectedTab(selectedTab, selectedFilters, isManageappPage, updatedBlacklistedSources)}
        selectedTab={selectedTab}
        selectedFilters={selectedFilters}
        scrollingContainer={scrollingContainerRef.current}
        generateImageCallback={(): void => {
          onTabClicked(StockPanelTabs.TEXT_TO_IMAGE);
        }}
        gridItemDimensions={getCustomDimensionForGridItems()}
        onInitialBatchLoaded={(): void => {
          setIsSearchLoading(false);
        }}
        onAiCtaClicked={aiCtaHandler}
        onNoResultsChange={setIsGridNoResults}
        clickBehaviour={clickBehaviour}
        enableDragToCanvas={enableDragToCanvas}
        onItemPointerDown={onItemPointerDown}
      />
    );
  };

  const getLoadingSkeletonRow = (): ReactElement<any>[] => {
    const skeletons: ReactElement<any>[] = [];
    for (let i = 0; i < TOTAL_SKELETONS_PER_ROW; i++) {
      const randomWidth = getRandomNumberInRange(MIN_SKELETON_WIDTH, MAX_SKELETON_WIDTH);
      skeletons.push(
        <li key={i}>
          <SkeletonBrick className="radius-12" height={CAROUSEL_ITEM_HEIGHT} width={randomWidth} />
        </li>
      );
    }

    return skeletons;
  };

  const getLoadingSkeletonForCarousel = (): ReactElement<any> => {
    return <ul className={`${styles.skeletonList} spacing-m-0 flex-h-row spacing-p-l-4 spacing-p-r-4`}>{getLoadingSkeletonRow()}</ul>;
  };

  const onViewAllClicked = (carouselType: StockCarouselType): void => {
    if (carouselType === StockCarouselType.AI) {
      aiCtaHandler('ai-all');
      return;
    }
    const tab = STOCK_CAROUSEL_TYPE_TO_TAB[carouselType];
    if (!tab) {
      return;
    }
    setSelectedFilters(getDefaultSelectedFilterForTab(tab, searchTerm));
    dispatch(selectTab(tabId, tab));
  };

  const getTextForCarouselLabel = (carouselType: StockCarouselType): ReactElement<any> => {
    switch (carouselType) {
      case StockCarouselType.STICKERS:
      case StockCarouselType.SHAPES:
      case StockCarouselType.PHOTOS:
      case StockCarouselType.AUDIOS:
      case StockCarouselType.ICONS:
      case StockCarouselType.RECENTS:
      case StockCarouselType.VIDEOS:
        return <Text val={getCarouselTitleText(carouselType)} className="content-body" type={TextType.BODY} size={TextSize.XSMALL} bold />;
      case StockCarouselType.AI:
        return (
          <div className={'flex-align-items-center flex-h-row gap-1'}>
            <Icon icon="icon-sparkle" size={IconSize.SIZE_ICON_16} className={'content-ai'} />
            <Text val={getCarouselTitleText(carouselType)} className={'content-ai'} type={TextType.BODY} size={TextSize.MD} bold />
          </div>
        );
      default:
        throw new Error(`Unhandled carousel type: ${carouselType}`);
    }
  };

  const getViewAllButtonForCarousel = (carouselType: StockCarouselType): ReactElement | null => {
    if (!shouldShowShowMoreCTA(carouselType)) {
      return null;
    }
    return (
      <Text
        className={`${styles.viewAllText} test-view-all`}
        val={window.i18next.t('pmwjs_carousel_view_all')}
        type={TextType.BODY}
        size={TextSize.XSMALL}
        onClick={(): void => {
          onViewAllClicked(carouselType);
        }}
        bold
      />
    );
  };

  const getCarouselHeaderContent = (carouselType: StockCarouselType, isLoading: boolean): ReactElement => {
    if (isLoading) {
      return <SkeletonBrick className="radius-round" height={TITLE_SKELETON_HEIGHT} width={TITLE_SKELETON_WIDTH} />;
    }
    return (
      <>
        {getTextForCarouselLabel(carouselType)}
        {getViewAllButtonForCarousel(carouselType)}
      </>
    );
  };

  const getCarouselUIForCarouselContent = (carouselContentMarkup: ReactElement<any>, carouselType: StockCarouselType, isLoading = false): ReactElement<any> => {
    return (
      <li className={`${styles.carouselRow} spacing-p-0 flex-v-row`} key={carouselType} data-testid={getTestIdForCarouselType(carouselType)}>
        <div className={`flex-h-row flex-justify-between spacing-p-r-4 ${isSmall ? 'spacing-p-l-4' : 'spacing-p-l-12'}`}>{getCarouselHeaderContent(carouselType, isLoading)}</div>
        {carouselContentMarkup}
      </li>
    );
  };

  const shouldShowLoadingCarouselForCarouselType = (carouselType: StockCarouselType): boolean => {
    switch (carouselType) {
      case StockCarouselType.RECENTS:
        return !recentStockLoaded;
      case StockCarouselType.AI:
        return !aiCarouselLoaded;
      case StockCarouselType.VIDEOS:
      case StockCarouselType.STICKERS:
      case StockCarouselType.SHAPES:
      case StockCarouselType.PHOTOS:
      case StockCarouselType.AUDIOS:
      case StockCarouselType.ICONS:
        return !loaded;
      default:
        throw new Error(`Unhandled carousel type: ${carouselType}`);
    }
  };

  const getCarouselViewRow = (carouselType?: StockCarouselType): ReactElement<any> | null => {
    if (carouselType) {
      if (shouldShowLoadingCarouselForCarouselType(carouselType)) {
        return getCarouselUIForCarouselContent(getLoadingSkeletonForCarousel(), carouselType, true);
      }
      const carousel = getCarouselForType(carouselType);
      return carousel ? getCarouselUIForCarouselContent(carousel, carouselType) : null;
    }
    return null;
  };

  const getCarouselForType = (carouselType: StockCarouselType): ReactElement<any> | null => {
    const items = carouselData.current[carouselType];
    if (!items) {
      return null;
    }
    return (
      <Carousel
        selectionGroupId={carouselItemsSelectionID.current}
        height={CAROUSEL_ITEM_HEIGHT}
        nextBtnRightPositioning={isSmall ? 16 : undefined}
        prevBtnLeftPositioning={isSmall ? 16 : undefined}
        maxSelectionNumber={props.maxSelectionNumber}
        controlButtonIconSize={IconSize.SIZE_ICON_16}
        controlButtonTopPositioning={6}
        maxItemAspectRatio={MAX_CAROUSEL_ITEM_ASPECT_RATIO}
        clickBehaviour={clickBehaviour}
        isItemDraggable={enableDragToCanvas}
        onItemPointerDown={onItemPointerDown}
        gridID={STOCK_CAROUSEL_TYPE_TO_ID[carouselType]}
        items={items}
        onSingleItemClick={onCTA}
      />
    );
  };

  const getCarouselsForTabs = (): (ReactElement<any> | null)[] => {
    const carouselRow: (ReactElement<any> | null)[] = [];
    if (shouldShowRecentsCarousel && isRecentStockAvailble && !searchTerm) {
      carouselRow.push(getCarouselViewRow(StockCarouselType.RECENTS));
    }
    getCarouselTabOrder(tabsToShow, searchTerm).forEach((tab) => {
      const carousels = STOCK_TABS_TO_CAROUSEL_TYPE_MAPPING[tab]?.map(getCarouselViewRow);
      carousels?.forEach((carouselMarkdown) => {
        carouselRow.push(carouselMarkdown);
      });
    });
    // The AI row renders only when the user has AI creations (see loadAiCarouselItems). When present it also stays
    // during a search — its data is loaded once and preserved across search re-fetches.
    if (isAiCarouselEnabled) {
      carouselRow.push(getCarouselViewRow(StockCarouselType.AI));
    }
    return carouselRow;
  };

  const isGridView = (): boolean => {
    return selectedTab !== StockPanelTabs.ALL;
  };

  /**
   * True while the panel shows a sub-view — an active search, or a tab drilled into from the multi-tab browse landing —
   * rather than the top-level browse. Back is only meaningful in a sub-view (or when a previous screen exists), so this
   * gates both the search bar's active state and whether the mobile header shows a back arrow.
   */
  const isInSubView = (): boolean => {
    return typeof searchTerm !== 'undefined' || (isGridView() && tabsToShow.length > 1);
  };

  const getTabBar = (): ReactElement | null => {
    if (tabsToShow.length <= 1) {
      return null;
    }
    return <StockMediaTabs tabs={tabsToShow} selectedTab={selectedTab} tabId={tabId} isMobile={isMobile} onTabClick={onTabClicked} />;
  };

  const getFilterRow = (): ReactElement | null => {
    if (!isGridView()) {
      return null;
    }
    return (
      <StockMediaFilters
        selectedTab={selectedTab}
        selectedFilters={selectedFilters}
        filtersForSelectedTab={stockTabsToFilterMapping.current[selectedTab]}
        keepFiltersHiddenForTab={keepFiltersHiddenForTab}
        allowedFiltersForTab={props.allowedFiltersForTab}
        onFilterItemClicked={onFilterChanged}
      />
    );
  };

  // Tabs + filters are one bar: it owns the 16px gap above its own bottom border, plus the scroll shadow
  // that separates the (non-scrolling) header from the content below — in both grid and carousel modes.
  const getTabFilterBar = (): ReactElement => {
    return (
      <div className={`${styles.tabFilterBar} spacing-p-b-4 ${showFilterShadow ? styles.tabFilterBarScrolled : ''}`}>
        {getTabBar()}
        {getFilterRow()}
      </div>
    );
  };

  const isAiCtaHiddenForSelectedTab = (): boolean => {
    return isGridView() && (selectedTab === StockPanelTabs.SHAPES || selectedTab === StockPanelTabs.ICONS);
  };

  // The AI CTA's 8px top margin tops the tab bar's 16px padding up to the 24px content gap; tabs
  // without a CTA add the 8px directly so the grid keeps the same 24px offset.
  const getGridTopSpacingClass = (): string => {
    return !isManageappPage && !isGridNoResults && isAiCtaHiddenForSelectedTab() ? 'spacing-m-t-2' : '';
  };

  const getContent = (): ReactElement<any> => {
    const gridHorizontalClass = isGridView() ? (isSmall ? 'spacing-p-l-4 spacing-p-r-4' : 'spacing-p-l-12 spacing-p-r-12') : '';
    const searchBarHorizontalClass = isTextToImageTabActive() && !isSmall ? 'spacing-m-l-12 spacing-m-r-12' : 'spacing-m-l-4 spacing-m-r-4';
    return (
      <PanelContent ref={panelContentRef} className={`flexbox flex-v-row ${isMobile ? '' : 'spacing-m-t-4'}`}>
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={isTextToImageTabActive() ? 'text-to-image-animation-container' : 'standard-animation-container'}
            className={searchBarHorizontalClass}
            initial={{height: 0, opacity: 0}}
            animate={{height: 'auto', opacity: 1}}
            transition={{duration: 0.3}}
            exit={{height: 'auto', opacity: 0}}
          >
            {getSearchBarContainer()}
          </motion.div>
        </AnimatePresence>
        {getTabFilterBar()}
        <div ref={scrollingContainerRef} className={styles.panel}>
          {getStockAiButton()}
          <div className={`${gridHorizontalClass} ${getGridTopSpacingClass()}`.trim()}>{getMainScreen()}</div>
        </div>
      </PanelContent>
    );
  };

  const isTextToImageTabActive = (): boolean => {
    return selectedTab === StockPanelTabs.TEXT_TO_IMAGE;
  };

  const getStockAiButton = (): ReactElement<any> | null => {
    if (isManageappPage) {
      return null;
    }
    // The TEXT_TO_IMAGE grid is itself the AI generator, so the AI CTA is redundant on it.
    if (isTextToImageTabActive()) {
      return null;
    }
    if (isAiCtaHiddenForSelectedTab()) {
      return null;
    }
    // The grid's no-results screen carries its own AI CTA after the info text, so the top button is
    // suppressed while that screen is showing to avoid a duplicate.
    if (isGridView() && isGridNoResults) {
      return null;
    }
    return (
      <StockAiButton
        isGridView={isGridView()}
        selectedTab={selectedTab}
        tabsToShow={tabsToShow}
        onAiImages={(): void => aiCtaHandler?.('ai-images')}
        onAiStickers={(): void => aiCtaHandler?.('ai-stickers')}
        onAiVideos={(): void => aiCtaHandler?.('ai-videos')}
        onAiVoice={(): void => aiCtaHandler?.('ai-voice')}
      />
    );
  };

  const getMainScreen = (): ReactElement<any> | null => {
    return isGridView() ? getStockGrid() : getCarousels();
  };

  /**
   * Drops the previous search's carousel items while preserving the two carousels that own their own
   * lifecycle (Recents and AI are loaded independently of the search). Every outcome of a search has
   * to go through this — a success repopulates from the response, and a failure must not leave the
   * previous search's items on screen labelled as the new one.
   */
  const resetSearchCarousels = (): void => {
    const recentStockItemsData = carouselData.current[StockCarouselType.RECENTS] ? [...carouselData.current[StockCarouselType.RECENTS]] : undefined;
    const aiCarouselData = carouselData.current[StockCarouselType.AI] ? [...carouselData.current[StockCarouselType.AI]] : undefined;
    carouselData.current = {};
    carouselData.current[StockCarouselType.RECENTS] = recentStockItemsData;
    carouselData.current[StockCarouselType.AI] = aiCarouselData;
  };

  const onCarouselStockReceived = (response: StockResponseCarousel): void => {
    ingestStockScoreDebug(response.scoreDebugPayload);
    resetSearchCarousels();

    const tabKeys = Object.keys(response) as StockPanelTabs[];
    tabKeys.forEach((tab) => {
      const carouselTypes = STOCK_TABS_TO_CAROUSEL_TYPE_MAPPING[tab];
      if (!carouselTypes || !response[tab]) {
        return;
      }
      response[tab]?.forEach((stockItem) => {
        switch (stockItem.type as StockSources) {
          case StockSources.PMW_ANIMATED_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForStickerItem(stockItem as PMWStockBackendAnimatedSticker),
            ];
            break;
          case StockSources.PMW_AI_ANIMATED_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForAiAnimatedStickerItem(stockItem as unknown as PMWStockBackendAiAnimatedSticker),
            ];
            break;
          case StockSources.PMW_AI_ICON:
            carouselData.current[StockCarouselType.ICONS] = [
              ...(carouselData.current[StockCarouselType.ICONS] ?? []),
              getCarouselDataForAiIconItem(stockItem as PMWStockBackendAiIcon),
            ];
            break;
          case StockSources.PMW_ICON:
            carouselData.current[StockCarouselType.ICONS] = [
              ...(carouselData.current[StockCarouselType.ICONS] ?? []),
              getCarouselDataForIconItem(stockItem as PMWStockBackendIcon),
            ];
            break;
          case StockSources.PMW_EXTRACTED_GETTY_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForExtractedGettyStickerItem(stockItem as PMWStockBackendExtractedGettySticker),
            ];
            break;
          case StockSources.PMW_AI_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForAiStickerItem(stockItem as PMWStockBackendAiSticker),
            ];
            break;
          case StockSources.PMW_STILL_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForStillStickerItem(stockItem as PMWStockBackendStillSticker),
            ];
            break;
          case StockSources.PMW_SHAPE:
            carouselData.current[StockCarouselType.SHAPES] = [
              ...(carouselData.current[StockCarouselType.SHAPES] ?? []),
              getCarouselDataForShapeItem(stockItem as PMWStockBackendShape),
            ];
            break;
          case StockSources.GETTY_IMAGE:
          case StockSources.STORYBLOCK_IMAGE:
          case StockSources.PIXABY_IMAGE:
            carouselData.current[StockCarouselType.PHOTOS] = [
              ...(carouselData.current[StockCarouselType.PHOTOS] ?? []),
              getCarouselDataForImageItem(stockItem, stockItem.type as StockSources),
            ];
            break;
          case StockSources.GETTY_VIDEO:
          case StockSources.STORYBLOCK_VIDEO:
          case StockSources.PIXABY_VIDEO: {
            const carouselDataForVideo = getCarouselDataForVideoItem(stockItem, stockItem.type as StockSources);
            if (carouselDataForVideo) {
              carouselData.current[StockCarouselType.VIDEOS] = [...(carouselData.current[StockCarouselType.VIDEOS] ?? []), carouselDataForVideo];
            }
            break;
          }
          case StockSources.STORYBLOCK_ILLUSTRATION:
          case StockSources.PIXABY_ILLUSTRATION:
          case StockSources.GETTY_ILLUSTRATION:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForClipartItem(stockItem as unknown as GridClipartItemBackEndData, stockItem.type as StockSources),
            ];
            break;
          case StockSources.STORYBLOCK_AUDIO:
            carouselData.current[StockCarouselType.AUDIOS] = [
              ...(carouselData.current[StockCarouselType.AUDIOS] ?? []),
              getCarouselDataForAudioItem(stockItem, CAROUSEL_ITEM_HEIGHT),
            ];
            break;
          default:
            throw new Error(`Unhandled stock source: ${stockItem.type ?? ''}`);
        }
      });
    });
    setLoaded(true);
    setIsSearchLoading(false);
  };

  const onAudioCategoriesReceived = (response: AudioCategoryBackendData[]): void => {
    audioFilterIDs.current = [];
    // The backend categories replace the tab's filters wholesale, so the seasonal pill is re-added
    // ahead of them rather than being dropped the moment the categories arrive.
    stockTabsToFilterMapping.current[StockPanelTabs.AUDIOS] = [
      ...getSeasonalFilter(),
      ...response.map((responseRow) => {
        audioFilterIDs.current.push(responseRow.id);
        return getStockFilterForAudioFilterBackendData(responseRow);
      }),
    ];
    setAreAudioCategoriesLoaded(true);
  };

  const loadAudioCategories = (): void => {
    getStockLibraryAudioCategories()
      .then(onAudioCategoriesReceived)
      .catch((e) => {
        console.error(e);
      });
  };

  /**
   * The carousel view's failure screen. The grid view has StockPanelNoResults for its empty case, but
   * the carousel view had no equivalent for either empty or failed - getCarouselForType returns null
   * for a carousel with no items, so its row renders nothing at all and a failure would be silent.
   */
  const getCarouselsLoadError = (): ReactElement => {
    return (
      <StockPanelMessage icon="icon-exclamation-triangle">
        <Text className="content-body text-center" val={window.i18next.t('pmwjs_stock_carousels_load_error')} type={TextType.BODY} size={TextSize.MD} />
      </StockPanelMessage>
    );
  };

  const getCarousels = (): ReactElement => {
    if (carouselFetchFailed) {
      return getCarouselsLoadError();
    }
    return <ul className={`${styles.carouselList} flex-v-row spacing-p-b-4`}>{getCarouselsForTabs()}</ul>;
  };

  const loadRecentStockItems = (): void => {
    if (!shouldShowRecentsCarousel) {
      setRecentStockLoaded(true);
      return;
    }
    getRecentStockItems()
      .then((res) => {
        const allowedRecentItems = res.filter((gridItem) => isRecentStockItemAllowed(gridItem, tabsToShow, updatedBlacklistedSources));
        carouselData.current[StockCarouselType.RECENTS] =
          allowedRecentItems.length >= MIN_STOCK_ITEMS_TO_DISPLAY
            ? allowedRecentItems.map((gridItem) => {
                return {
                  gridItemData: getGridItemForBackendData(gridItem),
                  width: gridItem.type === GRID_ITEM_TYPE.STOCK_AUDIO ? STOCK_AUDIO_GRID_ITEM_WIDTH : Number(gridItem.width ?? FALLBACK_GRID_ITEM_WIDTH),
                  height: gridItem.type === GRID_ITEM_TYPE.STOCK_AUDIO ? CAROUSEL_ITEM_HEIGHT : Number(gridItem.height ?? FALLBACK_GRID_ITEM_HEIGHT),
                } as CarouselItemData;
              })
            : undefined;
        dispatch(setRecentStockAvailability(!!carouselData.current[StockCarouselType.RECENTS]));
        setRecentStockLoaded(true);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const getCreateWithAiButtonCarouselItem = (): CarouselItemData => {
    return {
      gridItemData: {
        type: GRID_ITEM_TYPE.CUSTOM,
        id: AI_CREATE_WITH_AI_BUTTON_ID,
        showHoverOverlay: false,
        isDraggable: false,
        onClicked: (): void => {
          aiCtaHandler?.('ai-all');
        },
        children: (
          <div className={`${styles.createWithAiButton} gap-2`}>
            <Icon icon="icon-sparkle" size={IconSize.SIZE_ICON_20} className="content-ai" />
            <Text val={window.i18next.t('pmwjs_create_with_ai')} className="content-ai" type={TextType.BODY} size={TextSize.MD} bold />
          </div>
        ),
      },
      width: AI_CREATE_WITH_AI_BUTTON_WIDTH,
      height: CAROUSEL_ITEM_HEIGHT,
      hasFixedWidth: true,
    };
  };

  const loadAiCarouselItems = async (): Promise<void> => {
    if (!aiCarouselContentTypes.length || !isUserLoggedIn.current) {
      setAiCarouselLoaded(true);
      return;
    }
    const imagesPromise: Promise<AITextToImageBackendData[]> = aiCarouselContentTypes.includes(AiCarouselContentType.IMAGES)
      ? getGeneratedImagesForCarousel()
      : Promise.resolve([]);
    const videosPromise = aiCarouselContentTypes.includes(AiCarouselContentType.VIDEOS) ? getGeneratedAiVideos() : Promise.resolve([]);
    const voicesPromise: Promise<GenerateAISpeechResponse[]> = aiCarouselContentTypes.includes(AiCarouselContentType.AUDIO)
      ? getUserGeneratedSpeech(0, AI_VOICE_CAROUSEL_FETCH_LIMIT)
      : Promise.resolve([]);

    try {
      const [images, videos, voices] = await Promise.all([imagesPromise, videosPromise, voicesPromise]);
      const generatedItems: CarouselItemData[] = [
        ...images.map(getAiImageCarouselItem),
        ...videos.map(getCarouselDataForAiVideoItem),
        ...voices.map((voice) => getAiVoiceCarouselItem(voice, CAROUSEL_ITEM_HEIGHT)),
      ];
      // A user with no AI creations gets no AI row at all — the "Create with AI" card only leads a non-empty row.
      if (generatedItems.length > 0) {
        carouselData.current[StockCarouselType.AI] = [getCreateWithAiButtonCarouselItem(), ...generatedItems];
      }
    } catch (e) {
      console.error(e);
    } finally {
      setAiCarouselLoaded(true);
    }
  };

  const onModalUnmount = (): void => {
    clearSelection();

    dispatch(deleteTabStateForId(tabId));
  };

  const trackMediaItemSearch = (mustHaveSubfilter?: boolean): void => {
    if (!searchTerm) {
      return;
    }
    const filterTerm = selectedTab === StockPanelTabs.STICKERS ? getGATrackingFilterTerm(selectedFilters) : '';
    if (mustHaveSubfilter && !filterTerm) {
      return;
    }
    trackPosterBuilderGA4Events(`MediaModal_${filterTerm + getGATrackingSearchTerm(selectedTab)}Search`, {
      [GA4EventParamName.SEARCH_TERM]: searchTerm,
    });
  };

  useEffect(() => {
    if (isMobile && selectedTab === StockPanelTabs.VIDEOS) {
      getHasUserSeenMobileVideoPreview()
        .then((hasSeen) => {
          if (!hasSeen) {
            dispatch(
              updatePanelData({
                panelId,
                data: {
                  [PanelDataKeys.GROWL]: {
                    icon: 'icon-tap-and-hold',
                    text: window.i18next.t('pmwjs_video_preview_message_growl'),
                  } as PanelGrowlData,
                },
              })
            );
            setTimeout(() => {
              dispatch(
                updatePanelData({
                  panelId,
                  data: {
                    [PanelDataKeys.GROWL]: undefined,
                  },
                })
              );
              void markUserHasSeenMobileVideoPreview();
            }, MOBILE_VIDEO_PREVIEW_GROWL_VISIBLE_DURATION);
          }
        })
        .catch((e: unknown) => {
          console.error(e);
        });
    }
  }, [selectedTab]);

  useEffect(() => {
    primeStockScoreDebugAvailability();
  }, []);

  useEffect(() => {
    setLoaded(false);
    setCarouselFetchFailed(false); // a new search is a fresh attempt, not still-failed
    resetStockScoreDebug(); // drop the previous search's score entries
    const carouselTabTiers = getCarouselTiersForTabs(tabsToShow, updatedBlacklistedSources);
    if (Object.keys(carouselTabTiers).length === 0) {
      return undefined;
    }
    // Drop a slower earlier search's results if a newer search has since started, so the latest search always wins.
    let isStale = false;
    getStockForCarousel({
      tabTiers: carouselTabTiers,
      searchTerm,
      debugScores: isStockScoreDebugActive(),
    })
      .then((response): void => {
        if (isStale) {
          return;
        }
        onCarouselStockReceived(response);
      })
      .catch((e): void => {
        if (isStale) {
          return;
        }
        // Land the panel on a rendered failure message rather than an eternal skeleton. `loaded`
        // gates shouldShowLoadingCarouselForCarouselType for every tab and was only ever set
        // true by onCarouselStockReceived, so a rejected fetch left every carousel row spinning
        // forever - no message, no empty state, no retry.
        //
        // The reset is what keeps this honest: carouselData is a ref that only onCarouselStockReceived
        // ever cleared, so stopping the spinner without it would render the PREVIOUS search's items
        // under the new search term. And this reads as an explicit failure rather than as "no results"
        // because the two have different remedies - a failed fetch is worth retrying, an empty result
        // set is not. Note the failure is cached for 5 minutes (StockCarouselFlow maps it to
        // CACHED_BRIEFLY, which the kickoff replays verbatim), so the copy asks for a wait rather
        // than offering a retry that would re-serve this same stored failure.
        resetSearchCarousels();
        setCarouselFetchFailed(true);
        setLoaded(true);
        setIsSearchLoading(false);
        console.error(e);
      });
    return (): void => {
      isStale = true;
    };
  }, [searchTerm]);

  // A new search presents its results from the top, not wherever the previous results were scrolled to.
  useEffect(() => {
    if (scrollingContainerRef.current) {
      scrollingContainerRef.current.scrollTop = 0;
    }
  }, [searchTerm]);

  // Depends on the search alone: a hand-toggled pill must survive until the search itself changes.
  useEffect(() => {
    setSelectedFilters((currentFilters) => withSeasonalFilterResetForSearch(currentFilters, selectedTab, searchTerm));
  }, [searchTerm]);

  useEffect(() => {
    dispatch(setStockGetterOptions(getOptionsForStockGetter(selectedFilters, selectedTab, audioFilterIDs.current)));
  }, [selectedFilters, selectedTab, audioFilterIDs.current]);

  useEffect(() => {
    trackMediaItemSearch();
  }, [selectedTab, searchTerm]);

  useEffect(() => {
    if (selectedTab === StockPanelTabs.STICKERS && selectedFilters.length) {
      trackMediaItemSearch(true);
    }
  }, [selectedFilters]);

  useEffect(() => {
    dispatch(selectTab(tabId, props.defaultSearchTab ?? tabsToShow[0]));
    dispatch(
      updateMaxSelectionNumberForGrid({
        gridSelectionGroupId: carouselItemsSelectionID.current,
        maxSelectionNumber: props.maxSelectionNumber ?? 0,
      })
    );
    loadAudioCategories();
    loadRecentStockItems();
    void loadAiCarouselItems();

    return (): void => {
      onModalUnmount();
      // A pre-fill set for an AI box that never mounted (e.g. ai-all opened, then closed) must not leak into unrelated later opens.
      clearPendingAiPromptBoxPreFills();
    };
  }, [panelId]);

  // The desktop popup keeps this panel mounted while hidden, so the mount-time load never re-runs. Refresh a stale
  // Recents carousel when the popup closes — the row is current on the next open without ever mutating a visible panel.
  useEffect(() => {
    if (props.isPopupOpen === false && recentsNeedRefresh.current) {
      recentsNeedRefresh.current = false;
      loadRecentStockItems();
    }
  }, [props.isPopupOpen]);

  useEffect(() => {
    const scrollContainer = scrollingContainerRef.current;
    if (!scrollContainer) {
      return undefined;
    }
    let rafId = 0;
    const updateShadow = (): void => {
      setShowFilterShadow(scrollContainer.scrollTop > SCROLL_SHADOW_THRESHOLD);
    };
    const onScroll = (): void => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateShadow);
    };
    updateShadow();
    scrollContainer.addEventListener('scroll', onScroll, {passive: true});
    return (): void => {
      cancelAnimationFrame(rafId);
      scrollContainer.removeEventListener('scroll', onScroll);
    };
  }, []);

  const getHeader = (): ReactElement<PanelHeaderProps> | undefined => {
    if (props.showAiImagesHeader) {
      return getAiImagesHeader();
    }
    if (!isMobile) {
      return getDesktopHeader();
    }
    const canGoBack = props.onBack !== undefined || isInSubView();
    return <PanelHeader title={window.i18next.t('pmwjs_explore_media')} showBackButton={canGoBack} onBackButton={onBackButton} showCloseButton onClose={onClose} />;
  };

  const getAiImagesHeader = (): ReactElement<PanelHeaderProps> => {
    return (
      <PanelHeader
        titleClasses="_fit-width content-ai body-l-bold"
        leftIconProps={{icon: 'icon-ai-image', size: IconSize.SIZE_ICON_20, className: `content-ai ${props.onBack !== undefined ? '' : 'spacing-m-l-10'}`}}
        title={window.i18next.t('pmwjs_ai_images')}
        showCloseButton
        onClose={onClose}
        showBackButton={props.onBack !== undefined}
        onBackButton={onBackButton}
        forceCenterAlignTitleWithActionItems
        isOpenedInModal={isMobile}
      />
    );
  };

  const getDesktopHeader = (): ReactElement<PanelHeaderProps> | undefined => {
    if (props.desktopHeaderTitle === undefined) {
      return undefined;
    }
    return (
      <PanelHeader
        title={props.desktopHeaderTitle}
        titleClasses="ct-neutral-primary"
        titleTextSize={TextSize.LG}
        backButtonIconSize={IconSize.SIZE_ICON_16}
        className="_borderless spacing-p-l-4 spacing-p-t-4 spacing-m-b-4"
        size={HeaderSize.SMALL}
        showCloseButton={false}
        showBackButton
        onBackButton={onBackButton}
      />
    );
  };

  const handleAddFromAi = (items: ElementData[]): void | Promise<void> => {
    return props.onAddSelected(items);
  };

  const getAiPanelForMobile = (): ReactElement | null => {
    if (mobileAiPanelOpen === 'ai-all') {
      return (
        <StockAiPanel
          key="ai-all"
          panelId={STOCK_AI_PANEL_ID}
          aiCarouselContentTypes={aiCarouselContentTypes}
          shouldLoadDataUrlImage={props.shouldLoadDataUrlImage}
          enableDragToCanvas={enableDragToCanvas}
          enableMultiSelection={enableMultiSelection}
          maxSelectionNumber={props.maxSelectionNumber}
          selectionCtaText={props.selectionFooterCtaText}
          addInBulk={props.addInBulk}
          onAddSelected={handleAddFromAi}
          onOpenAiTool={onOpenAiToolFromAiPanel}
          onBack={backToStockPanel}
          onClose={onClose}
        />
      );
    }
    if (mobileAiPanelOpen === 'ai-images' || mobileAiPanelOpen === 'ai-stickers') {
      return (
        <AiImagePanel
          key={mobileAiPanelOpen}
          panelId={STOCK_PANEL_AI_IMAGE_PANEL_ID}
          mediaType={mobileAiPanelOpen === 'ai-stickers' ? MediaTypePrompt.STICKER : (props.defaultAiImageMediaType ?? MediaTypePrompt.PHOTO)}
          shouldLoadDataUrlImage
          onAddSelected={handleAddFromAi}
          onBack={onAiToolBack}
          onClose={onClose}
          isOpenedInPopOver={!isMobile}
          showBackButton={!(isMobile && isAiPanelOpenedDirectly.current)}
          showEditWithAiOnGeneratedImages={props.showEditWithAiOnGeneratedImages}
          maxSelectionNumber={props.maxSelectionNumber}
          addInBulk={props.addInBulk}
          enableMultiSelection={enableMultiSelection}
        />
      );
    }
    if (mobileAiPanelOpen === 'ai-videos') {
      return (
        <AiVideoPanel
          key="ai-videos"
          panelId={STOCK_PANEL_AI_VIDEO_PANEL_ID}
          maxSelectionNumber={props.maxSelectionNumber}
          enableMultiSelection={enableMultiSelection}
          shouldReplaceOnGenerate={props.shouldReplaceOnGenerate}
          onAddSelected={handleAddFromAi}
          onBack={onAiToolBack}
          onClose={onClose}
          isOpenedInPopOver={!isMobile}
          showBackButton={!(isMobile && isAiPanelOpenedDirectly.current)}
        />
      );
    }
    if (mobileAiPanelOpen === 'ai-voice') {
      return (
        <AiVoicePanel
          key="ai-voice"
          panelId={STOCK_PANEL_AI_VOICE_PANEL_ID}
          onBack={onAiToolBack}
          onClose={onClose}
          isOpenedInPopOver={!isMobile}
          // Replace flows pick a single voice, so they keep the per-item path and a capped selection; plain adds batch like the AI Videos panel.
          maxSelectionNumber={props.onAddVoiceItem ? 1 : props.maxSelectionNumber}
          onAddAudioItems={
            props.onAddVoiceItem
              ? undefined
              : async (itemIds): Promise<void> => {
                  onClose();
                  await addGeneratedVoicesToDesign(itemIds.map(Number));
                }
          }
          onAddAudioItem={async (itemId): Promise<void> => {
            await (props.onAddVoiceItem ?? addGeneratedVoiceToDesign)(Number(itemId));
            // A replace is done after one pick, so the panel dismisses like the stock grid's pick does.
            if (props.onAddVoiceItem) {
              onClose();
            }
          }}
        />
      );
    }
    return null;
  };

  const aiPanelForMobile = getAiPanelForMobile();

  // The panel stays mounted (hidden) while a mobile AI panel shows — mirroring the desktop popup —
  // so Back restores the exact browse state instead of remounting mid-animation inside the bottom sheet.
  return (
    <>
      <div className={aiPanelForMobile === null ? styles.panelHolder : styles.panelHolderHidden}>
        <Panel key={panelId} panelId={panelId} panelHeader={getHeader()} panelContent={getContent()} panelFooter={getFooter()} />
      </div>
      {aiPanelForMobile}
    </>
  );
}